/**
 * Custom MCP servers: remote MCP endpoints an organization admin connects on
 * Compliance → Integrations, whose allowed tools eve may then use.
 *
 * Same idiom as `useIntegrations` in `state/governance.ts`: one per-org
 * cached atom over the workspace server's
 * `/api/amoreg/regulation/custom-mcp-servers` routes, which check the
 * connection (create, edit, refresh) and forward to the domain API, where
 * the tenant guard and the admin check live. Nothing here decides who may
 * change what.
 *
 * A secret travels browser → workspace server once, on create or edit, and
 * never comes back: the wire carries only `hasSecret`.
 *
 * @module state/customMcpServers
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { complianceQueryAtom } from "./complianceLive";
import { fetchComplianceJson } from "./complianceFetch";
import type { OrgProfileUnavailableReason } from "./orgProfile";

export const CUSTOM_MCP_AUTH_TYPES = ["none", "bearer", "header"] as const;
export type CustomMcpAuthType = (typeof CUSTOM_MCP_AUTH_TYPES)[number];

/**
 * Who a server is for: the whole organization (an admin's to change), or
 * only the person who connected it (theirs alone; nobody else sees it).
 */
export const CUSTOM_MCP_SCOPES = ["organization", "personal"] as const;
export type CustomMcpScope = (typeof CUSTOM_MCP_SCOPES)[number];

const CustomMcpToolSchema = Schema.Struct({
  name: Schema.String,
  description: Schema.String,
  /** `read` only when the server marks the tool read-only. */
  effect: Schema.Literals(["read", "write"]),
  /** The admin's choice; write tools start switched off. */
  allowed: Schema.Boolean,
});
export type CustomMcpTool = typeof CustomMcpToolSchema.Type;

const CustomMcpServerWireSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  url: Schema.String,
  authType: Schema.Literals(CUSTOM_MCP_AUTH_TYPES),
  headerName: Schema.NullOr(Schema.String),
  hasSecret: Schema.Boolean,
  enabled: Schema.Boolean,
  tools: Schema.Array(CustomMcpToolSchema),
  lastCheckedAt: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  /** Absent on an API that predates personal servers: those are all the organization's. */
  scope: Schema.optional(Schema.Literals(CUSTOM_MCP_SCOPES)),
  /** Whether the caller may change it; absent on an older API, read as no. */
  canManage: Schema.optional(Schema.Boolean),
});
type CustomMcpServerWire = typeof CustomMcpServerWireSchema.Type;

export type CustomMcpServer = Omit<CustomMcpServerWire, "scope" | "canManage"> & {
  readonly scope: CustomMcpScope;
  /** Computed for the caller: an admin for an organization server, the owner for a personal one. */
  readonly canManage: boolean;
};

export interface CustomMcpServerList {
  readonly servers: ReadonlyArray<CustomMcpServer>;
}

/** The wire row as the client works with it; a missing permission fails closed. */
export const normalizeCustomMcpServer = (wire: CustomMcpServerWire): CustomMcpServer => ({
  ...wire,
  scope: wire.scope ?? "organization",
  canManage: wire.canManage ?? false,
});

const decodeList = (input: unknown) =>
  Schema.decodeUnknownEffect(Schema.Struct({ servers: Schema.Array(CustomMcpServerWireSchema) }))(
    input,
  ).pipe(
    Effect.map((list): CustomMcpServerList => ({
      servers: list.servers.map(normalizeCustomMcpServer),
    })),
  );
const decodeServerWire = Schema.decodeUnknownSync(CustomMcpServerWireSchema);
const decodeServer = (input: unknown): CustomMcpServer =>
  normalizeCustomMcpServer(decodeServerWire(input));

/** A connection as the admin entered it; the workspace server checks it before storing. */
export interface CustomMcpConnectionInput {
  readonly url: string;
  readonly authType: CustomMcpAuthType;
  /** Only for `header`; null otherwise. */
  readonly headerName: string | null;
  /** Null on edit keeps the stored secret; never sent for `none`. */
  readonly secret: string | null;
}

export interface CustomMcpServerCreateInput extends CustomMcpConnectionInput {
  readonly name: string;
  /** Organization servers need an admin; any member may add a personal one. */
  readonly scope: CustomMcpScope;
}

export interface CustomMcpServerUpdateInput {
  readonly name?: string;
  readonly enabled?: boolean;
  /** Replaces the allowed set: names of stored tools. */
  readonly allowedTools?: ReadonlyArray<string>;
  /** A changed connection; the workspace server re-checks it and re-reads the tools. */
  readonly connection?: CustomMcpConnectionInput;
}

/** A write's outcome: the stored server (null after a removal), or what went wrong. */
export type CustomMcpResult =
  | { readonly ok: true; readonly server: CustomMcpServer | null }
  | { readonly ok: false; readonly message: string };

/** Named as on every other regulation route; the workspace server also accepts `?org=`. */
export const CUSTOM_MCP_ORG_PARAM = "organizationId";

/** `…/custom-mcp-servers[/refresh]?organizationId=…[&serverId=…]` on the workspace server. */
export const customMcpServersUrl = (
  organizationId: string,
  options: { readonly serverId?: string; readonly action?: "refresh" } = {},
): string =>
  resolvePrimaryEnvironmentHttpUrl(
    `/api/amoreg/regulation/custom-mcp-servers${options.action === "refresh" ? "/refresh" : ""}`,
    {
      [CUSTOM_MCP_ORG_PARAM]: organizationId,
      ...(options.serverId === undefined ? {} : { serverId: options.serverId }),
    },
  );

/**
 * The JSON a connection becomes on the wire. Header name only for a custom
 * header; the secret only when one was typed — omitted, the stored one stays.
 */
export const connectionBody = (connection: CustomMcpConnectionInput) => ({
  url: connection.url.trim(),
  authType: connection.authType,
  headerName: connection.authType === "header" ? (connection.headerName?.trim() ?? null) : null,
  ...(connection.authType !== "none" && connection.secret !== null && connection.secret.length > 0
    ? { secret: connection.secret }
    : {}),
});

/** The create body: the name plus the connection, flat, as the workspace server parses it. */
export const createBody = (input: CustomMcpServerCreateInput) => ({
  name: input.name.trim(),
  scope: input.scope,
  ...connectionBody(input),
});

/** The update body: only the fields being changed. */
export const updateBody = (input: CustomMcpServerUpdateInput) => ({
  ...(input.name === undefined ? {} : { name: input.name.trim() }),
  ...(input.enabled === undefined ? {} : { enabled: input.enabled }),
  ...(input.allowedTools === undefined ? {} : { allowedTools: [...input.allowedTools] }),
  ...(input.connection === undefined ? {} : { connection: connectionBody(input.connection) }),
});

/** The `message` of a JSON error body (the connection check's verdict, a duplicate name). */
async function errorMessage(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json();
    const message =
      typeof body === "object" && body !== null && "message" in body
        ? (body as { message?: unknown }).message
        : undefined;
    return typeof message === "string" && message.trim().length > 0 ? message.trim() : null;
  } catch {
    return null;
  }
}

/**
 * One write against the browser routes. Resolves, never rejects: a refusal
 * carries the server's own words when it sent any, a fixed sentence when it
 * did not.
 */
export async function sendCustomMcpRequest(request: {
  readonly method: "POST" | "DELETE";
  readonly url: string;
  readonly body?: unknown;
  /** What to say when the server gives no reason. */
  readonly fallback: string;
}): Promise<CustomMcpResult> {
  try {
    const response = await fetch(request.url, {
      method: request.method,
      credentials: "include",
      ...(request.body === undefined
        ? {}
        : {
            headers: { "content-type": "application/json" },
            body: JSON.stringify(request.body),
          }),
    });
    if (!response.ok) {
      const message = await errorMessage(response);
      if (message !== null) return { ok: false, message };
      return {
        ok: false,
        message:
          response.status === 403
            ? "Only organization admins can change custom MCP servers."
            : request.fallback,
      };
    }
    if (request.method === "DELETE") return { ok: true, server: null };
    try {
      return { ok: true, server: decodeServer(await response.json()) };
    } catch {
      // Stored, but the answer is unreadable: the list re-read shows it.
      return { ok: true, server: null };
    }
  } catch {
    return { ok: false, message: request.fallback };
  }
}

/**
 * Servers as the list read returned them, with any written since laid over:
 * a write's answer is newer than the list until the list is read again (a
 * new snapshot), so a ticked box does not flick back while that read runs.
 */
export function overlayWrittenServers(
  servers: ReadonlyArray<CustomMcpServer>,
  written: Readonly<Record<string, CustomMcpServer | null>>,
): ReadonlyArray<CustomMcpServer> {
  const known = new Set(servers.map((server) => server.id));
  const kept = servers.flatMap((server) => {
    if (!(server.id in written)) return [server];
    const latest = written[server.id];
    return latest === null || latest === undefined ? [] : [latest];
  });
  const added = Object.values(written).filter(
    (server): server is CustomMcpServer => server !== null && !known.has(server.id),
  );
  return [...kept, ...added];
}

const unavailableMessage = (reason: OrgProfileUnavailableReason): string =>
  reason === "unconfigured"
    ? "Custom MCP servers are not available on this deployment."
    : "Custom MCP servers could not be loaded.";

const serversAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(
    organizationId,
    organizationId.length === 0
      ? Effect.succeed(null)
      : fetchComplianceJson(customMcpServersUrl(organizationId), decodeList),
  ).pipe(Atom.withLabel(`web-amoreg:custom-mcp-servers:${organizationId}`)),
);

export interface CustomMcpServersView {
  /** Null until read (or when the read failed — then `error` says so). */
  readonly servers: ReadonlyArray<CustomMcpServer> | null;
  readonly isPending: boolean;
  /** Why the list could not be read; null when it was. */
  readonly error: string | null;
  /** Check the connection, store it and its tools; the result carries the new server. */
  readonly create: (input: CustomMcpServerCreateInput) => Promise<CustomMcpResult>;
  readonly update: (
    serverId: string,
    input: CustomMcpServerUpdateInput,
  ) => Promise<CustomMcpResult>;
  /** Re-read the server's tool catalogue with its stored connection. */
  readonly refresh: (serverId: string) => Promise<CustomMcpResult>;
  readonly remove: (serverId: string) => Promise<CustomMcpResult>;
  /** Read the list again. */
  readonly reload: () => void;
}

export function useCustomMcpServers(organizationId: string | null): CustomMcpServersView {
  const atom = useMemo(() => serversAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailable =
    data !== null && "unavailable" in data
      ? data.unavailable
      : result._tag === "Failure"
        ? ("error" as const)
        : null;
  const snapshot = data !== null && !("unavailable" in data) ? data : null;

  // Writes since `base` was the list snapshot; a new snapshot supersedes them.
  const [written, setWritten] = useState<{
    readonly base: CustomMcpServerList | null;
    readonly servers: Readonly<Record<string, CustomMcpServer | null>>;
  }>({ base: null, servers: {} });

  const reload = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const settle = useCallback(
    (serverId: string | null, outcome: CustomMcpResult, removed = false) => {
      if (!outcome.ok) return outcome;
      const id = outcome.server?.id ?? serverId;
      if (id !== null && (outcome.server !== null || removed)) {
        setWritten((current) => ({
          base: snapshot,
          servers: {
            ...(current.base === snapshot ? current.servers : {}),
            [id]: removed ? null : outcome.server,
          },
        }));
      }
      reload();
      return outcome;
    },
    [reload, snapshot],
  );

  const create = useCallback(
    async (input: CustomMcpServerCreateInput): Promise<CustomMcpResult> => {
      if (!organizationId) return { ok: false, message: "No organization selected." };
      const outcome = await sendCustomMcpRequest({
        method: "POST",
        url: customMcpServersUrl(organizationId),
        body: createBody(input),
        fallback: "The server could not be connected.",
      });
      return settle(null, outcome);
    },
    [organizationId, settle],
  );

  const update = useCallback(
    async (serverId: string, input: CustomMcpServerUpdateInput): Promise<CustomMcpResult> => {
      if (!organizationId) return { ok: false, message: "No organization selected." };
      const outcome = await sendCustomMcpRequest({
        method: "POST",
        url: customMcpServersUrl(organizationId, { serverId }),
        body: updateBody(input),
        fallback:
          input.connection === undefined
            ? "The server could not be updated."
            : "The server could not be connected.",
      });
      return settle(serverId, outcome);
    },
    [organizationId, settle],
  );

  const refresh = useCallback(
    async (serverId: string): Promise<CustomMcpResult> => {
      if (!organizationId) return { ok: false, message: "No organization selected." };
      const outcome = await sendCustomMcpRequest({
        method: "POST",
        url: customMcpServersUrl(organizationId, { serverId, action: "refresh" }),
        body: {},
        fallback: "The tools could not be refreshed.",
      });
      return settle(serverId, outcome);
    },
    [organizationId, settle],
  );

  const remove = useCallback(
    async (serverId: string): Promise<CustomMcpResult> => {
      if (!organizationId) return { ok: false, message: "No organization selected." };
      const outcome = await sendCustomMcpRequest({
        method: "DELETE",
        url: customMcpServersUrl(organizationId, { serverId }),
        fallback: "The server could not be removed.",
      });
      return settle(serverId, outcome, true);
    },
    [organizationId, settle],
  );

  const servers = useMemo(
    () =>
      snapshot === null
        ? null
        : written.base === snapshot
          ? overlayWrittenServers(snapshot.servers, written.servers)
          : snapshot.servers,
    [snapshot, written],
  );

  return {
    servers: organizationId ? servers : null,
    isPending: result.waiting,
    error: organizationId && unavailable !== null ? unavailableMessage(unavailable) : null,
    create,
    update,
    refresh,
    remove,
    reload,
  };
}
